/* eslint-disable no-nested-ternary */
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  notification,
  Switch,
  Tooltip,
  Modal
} from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { QuestionCircleOutlined } from '@ant-design/icons';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import useQuotesContext from './QuotesContext';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import { preRequisitesLayout } from '../../utils/constants/formLayout';
import { mrpSwitchesDefault } from './mrpSwitchesDefault';
import ComparisonTable from './ComparisonTable';
import CGUp25 from '../../assets/CGUMRP.pdf';
import protectionMilaPdf from '../../assets/protection_mrpml.pdf';
import { formatValue } from '../../utils/formatValue';
import GoogleMapsIcon from '../../assets/images/icons/Google_Maps_icon.png';
import { isValidZipcode } from '../../utils/zipcode';
import {
  getMobilityOptions,
  getDeductibleOptions,
  getRcProOptions,
  getBusinessLossOptions,
  marketOptions,
  getLegalOptions
} from '../../utils/milaOptions';

const { Option } = Select;
const dateFormat = 'DD/MM/YYYY';

const useMRPFields = (form, supplier) => {
  const { dispatchAPI } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const { message } = useErrorMessage();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mrpType = params.get('mrpT');
  const { t } = useTranslation();
  const {
    codesNaf,
    formValues,
    setFormValues,
    exemptions,
    enums,
    postalCode,
    setPostalCode,
    estimatedCost,
    catNat,
    errorGetCities,
    setErrorGetCities
  } = useQuotesContext();
  const [addressSuggestion, setAddressSuggestion] = useState([]);
  const [cities, setCities] = useState([]);
  const [rCAllowed, setRCAllowed] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [customDeductibleAmount, setCustomDeductibleAmount] = useState(false);
  const [displayRcPro, setDisplayRcPro] = useState(false);
  const [displayRcProOptions, setDisplayRcProOptions] = useState(false);
  const [displayWantMarketValueOption, setdisplayWantMarketValueOption] =
    useState(false);
  const [visible, setVisible] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(
    supplier === 'ml'
      ? getRcProOptions()
      : [150000, 300000, 600000, 1000000, 1500000, 2000000]
  );
  const furniture_capital = form.getFieldValue('furniture_capital');
  const activities = form.getFieldValue('activities');
  const sites = form.getFieldValue('sites');
  const isAmendment = window.location.href.search('amendment');
  const chosenPckg = formValues?.[formValues?.package];
  const [checkedVal, setCheckedVal] = useState('');
  // const [modalVisible, setModalVisible] = useState(false);
  const [previousSites, setPreviousSites] = useState([]);

  const [displayMobilityGuarantee, setDisplayMobilityGuarantee] =
    useState(false);
  const [displaybusinessLossGuarantee, setDisplaybusinessLossGuarantee] =
    useState(false);
  const [displaymarketValue, setDisplaymarketValue] = useState(false);
  const [displayLegalProtection, setDisplayLegalProtection] = useState(false);
  const [previousActivities, setPreviousActivities] = useState([]);

  const getTotalSurfaceArea = () => {
    if (!formValues?.sites || !Array.isArray(formValues.sites)) {
      return 0;
    }

    return formValues.sites.reduce((total, site) => {
      return total + (site.surface_area || 0);
    }, 0);
  };

  const getLCI = () => {
    const surface = getTotalSurfaceArea() || 0;
    const turnover = formValues?.turnover || 0;
    const rcPro = formValues?.rc_pro_guarantee || 0;
    const fireCapital = formValues?.furniture_capital || 0;

    let lci = 5900000;

    if (
      surface <= 100 &&
      turnover <= 300000 &&
      rcPro <= 1000000 &&
      fireCapital <= 100000
    ) {
      lci = 1000000;
    } else if (surface <= 500 && turnover <= 1000000 && fireCapital <= 500000) {
      lci = 3000000;
    }

    setFormValues({
      ...formValues,
      lci
    });

    return lci;
  };

  const sitesKey = useMemo(
    () => JSON.stringify(formValues?.sites),
    [formValues?.sites]
  );

  const lciValue = useMemo(
    () => getLCI(),
    [
      sitesKey,
      formValues?.turnover,
      formValues?.rc_pro_guarantee,
      formValues?.furniture_capital,
      formValues?.fireCapital,
      formValues?.surface_area
    ]
  );

  const supplierSpecificValues =
    supplier === 'ml'
      ? {
          assistance: true,
          legal_protection_mrp: false,
          archives_capital: true,
          value_capital: true,
          art_capital: false,
          theft_capital: true,
          object_of_value: true,
          lent_goods: true
        }
      : {};

  const defaultPackageValues = {
    PACKAGE_1: {
      fire_explosion_attack_climatic_event: formValues?.furniture_capital,
      theft_vandalism: formValues?.furniture_capital / 4,
      water_damage: formValues?.furniture_capital / 4,
      broken_windows: 1000,
      broken_machine: 1000,
      electric_damage: 1000,
      external_furniture: 0,
      chilled_merchandise: 0,
      ...mrpSwitchesDefault,
      ...supplierSpecificValues
    },
    PACKAGE_2: {
      fire_explosion_attack_climatic_event: formValues?.furniture_capital,
      theft_vandalism: formValues?.furniture_capital / 2,
      water_damage: formValues?.furniture_capital / 2,
      broken_windows: 2000,
      broken_machine: 2000,
      electric_damage: 2000,
      external_furniture: 1000,
      chilled_merchandise: 1000,
      ...mrpSwitchesDefault,
      ...supplierSpecificValues
    },
    PACKAGE_3: {
      fire_explosion_attack_climatic_event: formValues?.furniture_capital,
      theft_vandalism: formValues?.furniture_capital,
      water_damage: formValues?.furniture_capital,
      broken_windows: 4000,
      broken_machine: 4000,
      electric_damage: 4000,
      external_furniture: 2000,
      chilled_merchandise: 2000,
      ...mrpSwitchesDefault,
      ...supplierSpecificValues
    }
  };

  const suppliersMap = {
    cm: 'CMAM',
    wk: 'WAKAM',
    ml: 'MILA'
  };

  const turnoverLevel = {
    cm: '1.000.000',
    ml: '750.000'
  };

  const pdfProtection = supplier === 'ml' ? protectionMilaPdf : CGUp25;

  const maxTurnover = turnoverLevel[supplier] || '500.000';

  const supplierFullName = suppliersMap[supplier] || 'SMAB';

  const isMRB =
    formValues?.activities &&
    formValues?.activities[0] === 'Activité de bureau';

  const getFrequencyOptions = () => {
    if (supplier === 'ml' || (supplier === 'wk' && isMRB)) {
      return ['MONTHLY', 'YEARLY'];
    }
    return ['MONTHLY', 'QUARTERLY', 'BIANNUAL', 'YEARLY'];
  };

  const openNotificationWithIcon = (notificationType, value, descriptionKey) => {
    if (descriptionKey) {
      notification[notificationType]({
        message: t('contracts.form.warning_title'),
        description: t(descriptionKey),
        duration: 7,
        key: 'oneTime'
      });
    } else if (value) {
      notification[notificationType]({
        message: 'Notification de dérogation',
        description: t('help.activities'),
        duration: 7,
        key: 'oneTime'
      });
    } else {
      notification[notificationType]({
        message: t('contracts.form.contribution_adjustment_message'),
        key: 'oneTime'
      });
    }
  };

  const deductibleOptions =
    supplier === 'ml'
      ? getDeductibleOptions(formValues)
      : [1000, 750, 500, 250, 0];

  useEffect(() => {
    if (exemptions.includes(checkedVal)) {
      openNotificationWithIcon('warning', 'value');
    } // eslint-disable-next-line
  }, [exemptions.length]);

  useEffect(() => {
    if (estimatedCost && estimatedCost.cat_nat_risk) {
      form.setFieldsValue({ cat_nat_risk: estimatedCost.cat_nat_risk });
      setFormValues({
        ...formValues,
        cat_nat_risk: estimatedCost.cat_nat_risk,
        ...chosenPckg
      });
    }
    // eslint-disable-next-line
  }, [catNat]);

  useEffect(() => {
    form.setFieldsValue(defaultPackageValues);
    setFormValues({ ...formValues, ...defaultPackageValues, ...chosenPckg });
    // eslint-disable-next-line
  }, [furniture_capital]);

  useEffect(() => {
    const sitesChanged =
      sites?.length !== previousSites?.length ||
      sites?.some(
        (site, index) =>
          site?.protection_level !== previousSites?.[index]?.protection_level
      );

    const activitiesArr = Array.isArray(activities) ? activities : [];
    const prevActivitiesArr = Array.isArray(previousActivities)
      ? previousActivities
      : [];

    const activitiesChanged =
      activitiesArr.length !== prevActivitiesArr.length ||
      [...activitiesArr]
        .sort()
        .some((a, i) => a !== [...prevActivitiesArr].sort()[i]);

    if (sitesChanged || activitiesChanged) {
      setPreviousActivities(activities);
      setPreviousSites(sites);
      setRCAllowed(!isMRB);
      let miniDeductibleAmount = supplier === 'ml' ? 830 : 750;
      const selectedActivities = (activities || []).map(
        (a) => a.split(' - ')[0]
      );
      // eslint-disable-next-line array-callback-return
      (form.getFieldValue('sites') || []).map((values) => {
        if (values?.protection_level && values?.protection_level > 1) {
          miniDeductibleAmount = supplier === 'ml' ? 1130 : 1000;
          form.setFieldsValue({ deductible_amount: miniDeductibleAmount });
          setFormValues({
            ...formValues,
            deductible_amount: miniDeductibleAmount
          });
        }
      });
      selectedActivities.forEach((activityName) => {
        const activity = codesNaf.find((act) => act.name === activityName);
        if (activity?.mini_deductible_amount > miniDeductibleAmount) {
          miniDeductibleAmount =
            supplier === 'ml' ? 1730 : activity?.mini_deductible_amount;
          form.setFieldsValue({ deductible_amount: miniDeductibleAmount });
          setFormValues({
            ...formValues,
            deductible_amount: miniDeductibleAmount
          });
        }
        if (
          activity &&
          !activity?.display?.MRP_RCP.includes(supplierFullName)
        ) {
          setRCAllowed(false);
          setFormValues({
            ...formValues,
            rc_pro: false,
            rc_pro_guarantee: 0,
            exploitation_responsibility: false,
            social_mandate_responsibility: false
          });
          setDisplayRcPro(false);
          setDisplayRcProOptions(false);
        }
      });
    }
    // eslint-disable-next-line
  }, [activities, sites]);

  const getCommuneCode = (value, key) => {
    try {
      setIsLoading(true);
      if (value) {
        const communCode = cities.find(
          (city) => city.nom === value.split(' (')[0]
        ).code;
        form.setFieldsValue({
          sites: form.getFieldValue('sites').map((values, index) =>
            index === key
              ? {
                  ...values,
                  address: {
                    ...values.address,
                    commune_code: communCode
                  }
                }
              : { ...values }
          )
        });
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const setAddress = (key, data, commune_code) =>
    form.getFieldValue('sites').map((values, index) =>
      index === key
        ? {
            ...values,
            address: {
              number: data.result.address_components[0].long_name,
              street: data.result.address_components[1].long_name,
              administrative_area_level_2:
                data.result.address_components[3].long_name,
              postal_code: data.result.address_components[6].long_name,
              city: data.result.address_components[2].long_name,
              formatted_address: data.result.formatted_address,
              lat: data.result.geometry.location.lat,
              lng: data.result.geometry.location.lng,
              commune_code,
              address_autocomplete: ''
            }
          }
        : { ...values }
    );

  const getCity = async (postal_code, placeIdData, key) => {
    setIsCityLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/insee/cities/${postal_code}`
      });
      const tmpData = data
        .map((city) => {
          if (city.codesPostaux.length > 1) {
            return city.codesPostaux.map((codePostal) => ({
              nom: city.nom.toUpperCase(),
              codePostal,
              code: city.code
            }));
          }
          return {
            nom: city.nom.toUpperCase(),
            codePostal: city.codesPostaux[0],
            code: city.code
          };
        })
        .flat();
      setCities(tmpData);
      setIsCityLoading(false);
      setErrorGetCities(false);
      let commune_code = '';
      if (placeIdData) {
        if (tmpData) {
          commune_code = tmpData.find(
            (city) =>
              placeIdData.result.address_components[2].long_name.toUpperCase() ===
              city.nom
          ).code;
        }
        form.setFieldsValue({
          sites: setAddress(key, placeIdData, commune_code)
        });
        setFormValues({
          ...formValues,
          sites: setAddress(key, placeIdData, commune_code),
          ...chosenPckg
        });
      }
      return tmpData;
    } catch (e) {
      if (e.response) {
        message(e.response.status);
      }
      setIsCityLoading(false);
      const isNotFoundError = e.response?.status === 404;
      setErrorGetCities(!isNotFoundError);
      return [];
    }
  };

  const handleLegalProtectionChange = (value) => {
    const transformedData = {
      legal_protection_mrp: value >= 1,
      extended_legal_protection_mrp: value === 2
    };

    setFormValues({
      ...formValues,
      ...transformedData
    });
  };

  const handleCompanyStatus = (value) => {
    if (value) {
      setFilteredOptions(
        getRcProOptions().filter((option) => option <= 150000)
      );
      setFormValues({
        ...formValues,
        self_entreprised: true,
        rc_pro_guarantee: Math.min(formValues?.rc_pro_guarantee || 0, 150000)
      });
      form.setFieldsValue({
        rc_pro_guarantee: Math.min(formValues?.rc_pro_guarantee || 0, 150000)
      });
    } else {
      setFilteredOptions(getRcProOptions());
      setFormValues({
        ...formValues,
        self_entreprised: false
      });
    }
  };

  const handleRcProChange = (value, sup) => {
    const minRcAmount = sup === 'ml' ? 50000 : 150000;
    if (value) {
      setDisplayRcPro(true);
      setDisplayRcProOptions(true);
      setFormValues({
        ...formValues,
        rc_pro_guarantee: minRcAmount
      });
      form.setFieldsValue({
        rc_pro_guarantee: minRcAmount
      });
    } else {
      setFormValues({
        ...formValues,
        rc_pro: false,
        rc_pro_guarantee: 0,
        exploitation_responsibility: false,
        social_mandate_responsibility: false
      });
      form.setFieldsValue({
        rc_pro_guarantee: 0,
        rc_pro: false,
        exploitation_responsibility: false,
        social_mandate_responsibility: false
      });
      setDisplayRcPro(false);
      setDisplayRcProOptions(false);
    }
  };

  const handleWantMobilityGuaranteeChange = (value) => {
    if (value) {
      setDisplayMobilityGuarantee(true);
      setFormValues({
        ...formValues,
        mobility_guarantee: 5000
      });
      form.setFieldsValue({
        mobility_guarantee: 5000
      });
    } else {
      setFormValues({
        ...formValues,
        mobility_guarantee: 0
      });
      form.setFieldsValue({
        mobility_guarantee: 0
      });
      setDisplayMobilityGuarantee(false);
    }
  };

  const handleLegalProtectionDisplayChange = (value) => {
    if (value) {
      setDisplayLegalProtection(true);
      form.setFieldsValue({
        legal_protection_choice: 1
      });
      setFormValues({
        ...formValues,
        legal_protection_mrp: true,
        extended_legal_protection_mrp: false
      });
    } else {
      form.setFieldsValue({
        legal_protection_choice: 0
      });
      setFormValues({
        ...formValues,
        legal_protection_mrp: false,
        extended_legal_protection_mrp: false
      });
      setDisplayLegalProtection(false);
    }
  };

  const handleWantbusinessLossGuaranteeChange = (value) => {
    if (value) {
      setDisplaybusinessLossGuarantee(true);
      setFormValues({
        ...formValues,
        business_loss_guarantee: 12
      });
      form.setFieldsValue({
        business_loss_guarantee: 12
      });
      setdisplayWantMarketValueOption(true);
    } else {
      setFormValues({
        ...formValues,
        business_loss_guarantee: 0,
        market_value: 0
      });
      form.setFieldsValue({
        business_loss_guarantee: 0,
        market_value: 0
      });
      setDisplaymarketValue(false);
      setDisplaybusinessLossGuarantee(false);
    }
  };

  const handleWantmarketValueChange = (value) => {
    if (value) {
      setDisplaymarketValue(true);
      setFormValues({
        ...formValues,
        market_value: 60000
      });
      form.setFieldsValue({
        market_value: 60000
      });
    } else {
      setFormValues({
        ...formValues,
        market_value: 0
      });
      form.setFieldsValue({
        market_value: 0
      });
      setDisplaymarketValue(false);
    }
  };

  const handleBusinessLossChange = (value) => {
    if (value === 0) {
      setdisplayWantMarketValueOption(false);
      setFormValues({
        ...formValues,
        market_value: 0,
        business_loss_guarantee: 0
      });
      form.setFieldsValue({
        market_value: 0,
        business_loss_guarantee: value
      });
    } else {
      setFormValues({
        ...formValues,
        business_loss_guarantee: value
      });
      setdisplayWantMarketValueOption(true);
    }
  };

  const handlePostalCodeChange = async (event, key) => {
    const { value } = event.target;

    const siteArr = form.getFieldValue('sites').map((values, index) =>
      index === key
        ? {
            ...values,
            address: {
              ...values.address,
              city: undefined
            }
          }
        : { ...values }
    );

    form.setFieldValue(['sites', key, 'address', 'city'], undefined);
    setFormValues({
      ...formValues,
      sites: siteArr
    });

    if (isValidZipcode(value)) {
      setPostalCode(value);
      const tmpCities = await getCity(value.slice(0, 2));

      if (tmpCities) {
        const filteredCities = tmpCities.filter((city) => {
          let isMatching = false;

          if (city.codePostal.startsWith(value)) {
            isMatching = true;
          }

          return isMatching;
        });

        const filteredAdressIsEmpty = !filteredCities.length > 0;

        const newSiteArr = form.getFieldValue('sites').map((values, index) =>
          index === key && !filteredAdressIsEmpty
            ? {
                ...values,
                address: {
                  ...values.address,
                  city: `${filteredCities[0].nom} (${filteredCities[0].codePostal})`
                }
              }
            : { ...values }
        );

        if (!filteredAdressIsEmpty) {
          form.setFieldValue(
            ['sites', key, 'address', 'city'],
            `${filteredCities[0].nom} (${filteredCities[0].codePostal})`
          );
          setFormValues({
            ...formValues,
            sites: newSiteArr
          });
        }
      }
    }
  };

  const getAddressAutocomplete = async (address_autocomplete) => {
    if (address_autocomplete) {
      setIsLoading(true);
      try {
        const { data } = await dispatchAPI('GET', {
          url: `/places/${address_autocomplete}`
        });
        setAddressSuggestion(data?.predictions);
      } catch (e) {
        if (e.response) message(e.response.status);
      }
      setIsLoading(false);
    }
  };

  const getAddressPlaceId = async (placeId, key) => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/places/placeid/${placeId}`
      });
      if (data) {
        await getCity(
          data.result.address_components[6].long_name.substring(0, 2),
          data,
          key
        );
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const mrpFields = [
    {
      name: ['activities'],
      tooltip: {
        name: 'activities_params',
        params: { max: supplier === 'ml' ? 5 : 10 }
      },
      rules: [{ required: true }],
      hidden: mrpType === 'office',
      input: (
        <Select
          className="activities-select"
          mode="multiple"
          style={{ width: 550 }}
          options={codesNaf
            .filter((code) => code?.display?.MRP.includes(supplierFullName))
            .map(({ naf_code, name }) => ({
              value: `${name} - ${naf_code || 'N/A'}`,
              label: `${name} - ${naf_code || 'Naf non renseigné'}`
            }))}
          filterOption={(input, option) =>
            formatValue(option.label).indexOf(formatValue(input)) > -1
          }
          onChange={(value) =>
            setFormValues({
              ...formValues,
              activities: value
            })
          }
        />
      ),
      help:
        supplier === 'ml'
          ? exemptions.includes('activities')
            ? t('help.exemptions_activities_five')
            : exemptions.includes('derogatory_activity')
              ? t('help.derogatory_activity')
              : null
          : exemptions.includes('activities')
            ? t('help.exemptions_activities')
            : exemptions.includes('derogatory_activity')
              ? t('help.derogatory_activity')
              : null,
      step: 0
    },
    {
      name: ['turnover'],
      tooltip: {
        name: 'turnover_params',
        params: { max: maxTurnover }
      },
      rules: [
        { required: true },
        {
          validator: (_, value) =>
            formValues && value > 0 ? Promise.resolve() : Promise.reject()
        }
      ],
      input: <InputNumber min={0} style={{ width: 120 }} />,
      help: exemptions.includes('turnover') ? t('help.exemptions') : null,
      step: 0
    },
    {
      name: ['employees_number'],
      tooltip: true,
      tooltipName: 'employees_number_step20',
      rules: [{ required: true }],
      input: <InputNumber min={0} />,
      help: exemptions.includes('employees_number')
        ? t('help.exemptions')
        : null,
      step: 0
    },
    {
      name: ['number_claims'],
      tooltip: true,
      rules: [{ required: true }],
      input: <InputNumber min={0} />,
      help: exemptions.includes('number_claims') ? t('help.exemptions') : null,
      step: 0
    },
    ...(formValues && formValues.number_claims >= 1
      ? [
          {
            name: ['claims'],
            noLabel: false,
            step: 0,
            help: exemptions.includes(`claim_amount`)
              ? t('help.exemptions')
              : null,
            input: (
              <Form.List name="claims">
                {(fields) => (
                  <div>
                    {fields.map((field) => (
                      <Space
                        key={field.key}
                        style={{ display: 'flex', marginBottom: 8 }}
                        align="baseline"
                      >
                        <Form.Item
                          noStyle
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          name={[field.name, 'claim_date']}
                          key={[field.fieldKey, 'claim_date']}
                          rules={[
                            {
                              required: true,
                              message: 'Date du sinistre manquant'
                            }
                          ]}
                        >
                          <DatePicker
                            format={dateFormat}
                            style={{ minWidth: 200 }}
                            disabledDate={(current) => {
                              return current >= moment();
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          name={[field.name, 'claim_type']}
                          key={[field.fieldKey, 'claim_type']}
                          rules={[
                            {
                              required: true,
                              message: 'Type du sinistre manquant'
                            }
                          ]}
                        >
                          <Input
                            style={{ width: '200px' }}
                            placeholder="Type de sinistre"
                          />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          name={[field.name, 'claim_amount']}
                          key={[field.fieldKey, 'claim_amount']}
                          rules={[
                            {
                              required: true,
                              message: 'Montant du sinistre manquant'
                            }
                          ]}
                        >
                          <InputNumber
                            min={0}
                            style={{ width: '200px' }}
                            formatter={(value) => value && `${value}€`}
                            parser={(value) => value.replace('€', '')}
                            placeholder="Montant du sinistre"
                          />
                        </Form.Item>
                      </Space>
                    ))}
                  </div>
                )}
              </Form.List>
            )
          }
        ]
      : []),
    {
      name: ['company_been_insured'],
      tooltip: true,
      rules: [{ required: true }],
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      step: 0
    },
    ...(formValues && formValues.company_been_insured
      ? [
          {
            name: ['last_company_name'],
            input: <Input style={{ width: 500 }} />,
            rules: [{ required: true }],
            step: 0
          },
          {
            name: ['last_contract_number'],
            input: <Input style={{ width: 500 }} />,
            step: 0
          },
          {
            name: ['last_termination_date'],
            input: (
              <DatePicker
                format={dateFormat}
                style={{ minWidth: 200 }}
                onChange={(date) => {
                  setFormValues((prev) => {
                    const newValues = { ...prev, last_termination_date: date };
                    if (prev.termination_by_company) {
                      newValues.termination_date_by_company = date;
                    } else {
                      delete newValues.termination_date_by_company;
                    }
                    return newValues;
                  });
                }}
              />
            ),
            step: 0
          },
          {
            name: ['termination_by_company'],
            rules: [{ required: true }],
            input: (
              <PreRequisitesSwitch
                onChange={(checked) => {
                  setFormValues((prev) => {
                    const newValues = {
                      ...prev,
                      termination_by_company: checked
                    };
                    if (checked) {
                      newValues.termination_date_by_company =
                        prev.last_termination_date;
                    } else {
                      delete newValues.termination_date_by_company;
                    }
                    return newValues;
                  });
                }}
              />
            ),
            valuePropName: 'checked',
            tooltip: true,
            step: 0
          }
        ]
      : []),
    ...(formValues && formValues.termination_by_company
      ? [
          {
            name: ['termination_reason_by_company'],
            rules: [{ required: true }],
            input: (
              <Select style={{ width: 220 }}>
                {enums?.termination_reason_by_company &&
                  enums.termination_reason_by_company.map((e) => (
                    <Option value={e} key={e}>
                      {t(`contracts.form.${e}`)}
                      <Tooltip
                        title={t(`contracts.form.tooltip.${e}`, {
                          rate: supplier === 'ml' ? 20 : 15
                        })}
                        overlayInnerStyle={{ width: 500 }}
                      >
                        <QuestionCircleOutlined style={{ float: 'right' }} />
                      </Tooltip>
                    </Option>
                  ))}
              </Select>
            ),
            help: exemptions.includes('termination_by_company')
              ? t('help.exemptions')
              : null,
            step: 0
          }
        ]
      : []),
    ...(formValues &&
    formValues.termination_reason_by_company === 'termination_other'
      ? [
          {
            name: ['termination_reason_by_company_custom'],
            rules: [{ required: true }],
            step: 0
          }
        ]
      : []),
    {
      name: ['sites'],
      noLabel: true,
      help: exemptions.includes('number_sites') ? t('help.exemptions') : null,
      noStyle: true,
      input: (
        <Form.List
          name="sites"
          initialValue={[{ surface_area: 0, tenant_status: 'WITHOUT_CLAUSE' }]}
        >
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <div key={field.name}>
                  <Form.Item
                    {...field}
                    key={[field.name, 'address', 'address_autocomplete']}
                    name={[field.name, 'address', 'address_autocomplete']}
                    label={t('contracts.form.address.address_autocomplete')}
                    hasFeedback={isLoading}
                    {...(isLoading ? { validateStatus: 'validating' } : {})}
                  >
                    <Select
                      filterOption={false}
                      showSearch
                      showArrow={false}
                      notFoundContent={null}
                      onSearch={getAddressAutocomplete}
                      onSelect={async (selected) => {
                        const placeId = selected.split('#')[0];
                        await getAddressPlaceId(placeId, field.name);
                      }}
                      options={
                        addressSuggestion &&
                        addressSuggestion.map((predictions) => ({
                          label: predictions.description,
                          value: `${predictions.place_id}#${predictions.description}`
                        }))
                      }
                      autoComplete="anyrandomstring"
                    />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'address', 'number']}
                    key={[field.name, 'address', 'number']}
                    label={t('contracts.form.address.number')}
                    rules={[{ required: true }]}
                  >
                    <InputNumber />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'address', 'street']}
                    key={[field.name, 'address', 'street']}
                    label={t('contracts.form.address.street')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'address', 'additional']}
                    key={[field.name, 'address', 'additional']}
                    label={t('contracts.form.address.additional')}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'address', 'postal_code']}
                    key={[field.name, 'address', 'postal_code']}
                    label={t('contracts.form.address.postal_code')}
                    rules={[
                      () => ({
                        validator(_, value) {
                          if (!isValidZipcode(value)) {
                            return Promise.reject(
                              new Error(
                                'Le code postal doit être composé de 5 chiffres'
                              )
                            );
                          }
                          let blockedPostCode = false;
                          const selectedActivities = (activities || []).map(
                            (a) => a.split(' - ')[0]
                          );
                          selectedActivities.forEach((activityName) => {
                            const activity = codesNaf.find(
                              (act) => act.name === activityName
                            );
                            if (
                              activity?.excluded_postcode &&
                              activity?.excluded_postcode.length
                            ) {
                              activity.excluded_postcode.forEach((postcode) => {
                                if (value.startsWith(postcode))
                                  blockedPostCode = true;
                              });
                            }
                          });
                          if (!value || !blockedPostCode)
                            return Promise.resolve();
                          return Promise.reject(
                            new Error(
                              `Ce code postal n'est pas autorisé pour ${
                                activities.length === 1
                                  ? "l'activité sélectionnée"
                                  : 'les activités sélectionnées'
                              }`
                            )
                          );
                        }
                      }),
                      { required: true }
                    ]}
                    help={
                      exemptions.includes('postcode')
                        ? t('help.exemptions')
                        : null
                    }
                  >
                    <Input
                      style={{ width: '90px' }}
                      onChange={(event) =>
                        handlePostalCodeChange(event, field.name)
                      }
                      autoComplete="off"
                    />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'address', 'city']}
                    key={[field.name, 'address', 'city']}
                    label={t('contracts.form.address.city')}
                    rules={[{ required: true }]}
                    hasFeedback={isCityLoading}
                    {...(isCityLoading ? { validateStatus: 'validating' } : {})}
                  >
                    {!errorGetCities ? (
                      <Select
                        showSearch
                        onSelect={(selected) => {
                          getCommuneCode(selected, field.name);
                        }}
                        options={
                          cities &&
                          cities
                            .filter((city) => {
                              let isMatching = false;

                              if (city.codePostal.startsWith(postalCode)) {
                                isMatching = true;
                              }

                              return isMatching;
                            })
                            .map((city) => ({
                              label: `${city.nom} (${city.codePostal})`,
                              value: `${city.nom} (${city.codePostal})`
                            }))
                        }
                      />
                    ) : (
                      <Input />
                    )}
                  </Form.Item>
                  {formValues?.sites && formValues?.sites[0]?.address && (
                    <>
                      <a
                        className="form-map-link mrp-map-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${
                          formValues?.sites[0].address &&
                          formValues?.sites[0].address.lat
                        }0%2C${
                          formValues?.sites[0].address &&
                          formValues?.sites[0].address.lng
                        }&heading=-45&pitch=38&fov=80`}
                      >
                        {`Visualiser l'immeuble `}
                        <img src={GoogleMapsIcon} alt="icone Google Maps" />
                      </a>
                    </>
                  )}
                  <Form.Item
                    {...field}
                    name={[field.name, 'address', 'commune_code']}
                    key={[field.name, 'address', 'commune_code']}
                    label={t('contracts.form.commune_code')}
                    hidden
                  >
                    <Input disabled style={{ width: '90px' }} />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'surface_area']}
                    rules={[
                      { required: true, message: t('errors.form.REQUIRED') },
                      {
                        validator: (_, value) =>
                          formValues && value > 0
                            ? Promise.resolve()
                            : Promise.reject(new Error('Ce champs est requis.'))
                      }
                    ]}
                    key={[field.name, 'surface_area']}
                    label={t('contracts.form.surface_area')}
                    tooltip={t('contracts.form.tooltip.surface_area_mrp', {
                      max: supplier === 'ml' ? 500 : 400
                    })}
                    help={
                      exemptions.includes('sites') ? t('help.exemptions') : null
                    }
                  >
                    <InputNumber
                      min={0}
                      rules={[
                        { required: true, message: t('errors.form.REQUIRED') }
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'resident_status']}
                    key={[field.name, 'resident_status']}
                    label={t('contracts.form.resident_status')}
                    rules={[{ required: true }]}
                    help={
                      exemptions.includes('resident_status')
                        ? t('help.exemptions')
                        : null
                    }
                  >
                    <Radio.Group buttonStyle="solid">
                      <Radio.Button value="TENANT">
                        {t('contracts.form.options.TENANT')}
                      </Radio.Button>
                      <Radio.Button value="OWNER">
                        {t('contracts.form.options.OWNER')}
                      </Radio.Button>
                      <Radio.Button value="RENT_FREE">
                        {t('contracts.form.options.RENT_FREE')}
                      </Radio.Button>
                      {supplier === 'ml' && (
                        <>
                          <Radio.Button value="SUB_TENANT">
                            {t('contracts.form.options.SUB_TENANT')}
                          </Radio.Button>
                          <Radio.Button value="HOSTED_BY_TENANT">
                            {t('contracts.form.options.HOSTED_BY_TENANT')}
                          </Radio.Button>
                        </>
                      )}
                    </Radio.Group>
                  </Form.Item>
                  {formValues?.sites?.length &&
                    ['TENANT'].includes(
                      formValues?.sites[field.name]?.resident_status
                    ) && (
                      <Form.Item
                        {...field}
                        name={[field.name, 'tenant_status']}
                        key={[field.name, 'tenant_status']}
                        rules={[{ required: true }]}
                        label={t(
                          `contracts.form.tenant_status${
                            formValues?.sites[field.name]?.resident_status ===
                            'RENT_FREE'
                              ? '_rent_free'
                              : ''
                          }`
                        )}
                        tooltip={t('contracts.form.tooltip.tenant_status')}
                        help={
                          exemptions.includes('tenant_status')
                            ? t('help.exemptions')
                            : null
                        }
                      >
                        <Select style={{ width: 420 }}>
                          <Option value="TENANT_AGAINST_OWNER">
                            {t('contracts.form.options.TENANT_AGAINST_OWNER')}
                          </Option>
                          <Option value="OWNER_AGAINST_TENANT">
                            {t('contracts.form.options.OWNER_AGAINST_TENANT')}
                          </Option>
                          <Option value="NO_AGAINST">
                            {t('contracts.form.options.NO_AGAINST')}
                          </Option>
                          <Option value="OWNER_ACTING_TENANT">
                            {t('contracts.form.options.OWNER_ACTING_TENANT')}
                          </Option>
                          <Option value="WITHOUT_CLAUSE">
                            {t('contracts.form.options.WITHOUT_CLAUSE')}
                          </Option>
                        </Select>
                      </Form.Item>
                    )}
                  <Form.Item
                    {...field}
                    name={[field.name, 'isolated_site']}
                    key={[field.name, 'isolated_site']}
                    label={t('contracts.form.isolated_site')}
                    tooltip={t('contracts.form.tooltip.isolated_site')}
                    valuePropName="checked"
                    help={
                      exemptions.includes('isolated_site')
                        ? t('help.exemptions')
                        : null
                    }
                  >
                    <Switch
                      checkedChildren={t('quotes.form.switch.yes')}
                      unCheckedChildren={t('quotes.form.switch.no')}
                    />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'protection_level']}
                    key={[field.name, 'protection_level']}
                    hidden
                  >
                    <InputNumber />
                  </Form.Item>
                  <Form.Item>
                    {`${t('contracts.form.protection_level')} `}
                    <strong>
                      {form.getFieldsValue() &&
                        form.getFieldsValue().sites &&
                        form.getFieldsValue().sites.length > 0 &&
                        form.getFieldsValue().sites[0]?.protection_level}
                    </strong>
                  </Form.Item>
                  <Form.Item name="level_protection_modal">
                    <Button type="link" onClick={() => setVisible(true)}>
                      {t('contracts.form.modal_tab')}
                    </Button>
                    <Modal
                      title={t('contracts.form.modal_title')}
                      open={visible}
                      onCancel={() => setVisible(false)}
                      footer={null}
                      width={1200}
                    >
                      <iframe
                        height="900"
                        width="100%"
                        title="Niveau de protection"
                        src={pdfProtection}
                      />
                    </Modal>
                  </Form.Item>
                  {formValues?.sites?.length &&
                    formValues?.sites?.length > 1 && (
                      <Form.Item>
                        <Button
                          type="link"
                          danger
                          onClick={() => {
                            remove(field.name);
                          }}
                        >
                          {t('buttons.removeSite')}
                        </Button>
                      </Form.Item>
                    )}
                </div>
              ))}
              <Form.Item>
                <Button type="primary" onClick={() => add()}>
                  {t('buttons.addSite')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      ),
      step: 0
    },
    ...(supplier === 'wk'
      ? [
          {
            name: ['close_before_2300'],
            tooltip: false,
            input: <PreRequisitesSwitch />,
            help: exemptions.includes('close_before_2300')
              ? t('help.exemptions')
              : null,
            valuePropName: 'checked',
            step: 0
          },
          {
            name: ['monument'],
            label: ['monument_outside_prerequisites'],
            tooltip: false,
            input: <PreRequisitesSwitch />,
            help: exemptions.includes('monument') ? t('help.exemptions') : null,
            valuePropName: 'checked',
            step: 0
          },
          {
            name: ['closed_more_60_days'],
            tooltip: false,
            input: <PreRequisitesSwitch />,
            help: exemptions.includes('closed_more_60_days')
              ? t('help.exemptions')
              : null,
            valuePropName: 'checked',
            step: 0
          },
          {
            name: ['zone_ind_com_art'],
            tooltip: false,
            input: <PreRequisitesSwitch />,
            help: exemptions.includes('zone_ind_com_art')
              ? t('help.exemptions')
              : null,
            valuePropName: 'checked',
            step: 0
          },
          {
            name: ['renovation'],
            label: ['renovation_wk'],
            tooltip: false,
            input: <PreRequisitesSwitch />,
            help: exemptions.includes('renovation')
              ? t('help.exemptions')
              : null,
            valuePropName: 'checked',
            step: 0
          }
        ]
      : []),
    ...(supplier !== 'ml'
      ? [
          {
            name: ['is_in_liquidation_or_receivership'],
            label: ['is_in_liquidation_or_receivership'],
            tooltip: false,
            input: <PreRequisitesSwitch />,
            help: exemptions.includes('is_in_liquidation_or_receivership')
              ? t('help.exemptions')
              : null,
            valuePropName: 'checked',
            step: 0
          }
        ]
      : []),
    {
      step: 1,
      name: ['furniture_capital'],
      tooltip: true,
      help: exemptions.includes('furniture_capital')
        ? t('help.exemptions')
        : null,
      input: <InputNumber min={0} step={5000} />
    },
    {
      name: ['deductible_amount'],
      tooltip: true,
      tooltipName:
        supplier === 'ml' ? 'deductible_amount_ml' : 'deductible_amount',
      rules: [{ required: true }],
      input: (
        <Radio.Group
          buttonStyle="solid"
          onChange={(e) => {
            if (e.target.value === 0 && formValues?.key_man_guarantee) {
              form.setFieldsValue({ key_man_guarantee: false });
              setFormValues((prev) => ({ ...prev, key_man_guarantee: false }));
              openNotificationWithIcon(
                'warning',
                null,
                'contracts.form.key_man_guarantee_zero_deductible_warning'
              );
            }
          }}
        >
          {deductibleOptions.map((amount) => (
            <Radio.Button
              key={amount}
              onClick={() => setCustomDeductibleAmount(false)}
              value={amount}
            >
              {`${amount}€`}
            </Radio.Button>
          ))}

          {/* <Radio.Button
            onClick={() => {
              setCustomDeductibleAmount(true);
              setModalVisible(true);
            }}
            value={customDeductibleAmount && formValues?.deductible_amount}
          >
            {`Autre montant ${
              customDeductibleAmount && formValues?.deductible_amount
                ? `${formValues?.deductible_amount}€`
                : ''
            }`}
            {customDeductibleAmount && (
              <Modal
                open={modalVisible}
                centered
                width={400}
                title="Montant personnalisé"
                cancelText="Annuler"
                onOk={() => {
                  setModalVisible(false);
                }}
                onCancel={() => {
                  setModalVisible(false);
                  setCustomDeductibleAmount(false);
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <InputNumber
                    onChange={(change) => {
                      setFormValues((prevState) => ({
                        ...prevState,
                        deductible_amount: change
                      }));
                    }}
                    min={0}
                    defaultValue={0}
                    style={{ width: 80 }}
                  />
                </div>
              </Modal>
            )}
          </Radio.Button> */}
        </Radio.Group>
      ),
      help: exemptions.includes('deductible_amount')
        ? t('help.exemptions')
        : null,
      step: 1
    },
    {
      step: 1,
      name: ['comparison'],
      noStyle: true,
      noLabel: true,
      layout: {
        labelCol: { span: 0 },
        wrapperCol: { xs: 24, sm: 24, md: 24, lg: 24, xl: 24 }
      },
      input: (
        <ComparisonTable
          form={form}
          defaultPackageValues={defaultPackageValues}
          fields={[
            { name: ['fire_explosion_attack_climatic_event'], type: 'number' },
            { name: ['water_damage'], type: 'number' },
            { name: ['theft_vandalism'], type: 'number' },
            { name: ['broken_windows'], type: 'number' },
            { name: ['broken_machine'], type: 'number' },
            { name: ['electric_damage'], type: 'number' },
            { name: ['external_furniture'], type: 'number' },
            { name: ['chilled_merchandise'], type: 'number' },
            ...(supplier !== 'ml'
              ? [{ name: ['legal_protection_mrp'], type: 'fixed' }]
              : []),
            { name: ['catastrophe'], type: 'fixed' },
            { name: ['tech_catastrophe'], type: 'fixed' },
            { name: ['attack'], type: 'fixed' },
            ...(supplier !== 'ml'
              ? [
                  { name: ['tenant_responsibility'], type: 'fixed' },
                  { name: ['neighbours_appeal'], type: 'fixed' },
                  { name: ['tenant_appeal'], type: 'fixed' }
                ]
              : []),
            { name: ['lawsuit_defense'], type: 'fixed' },
            ...(supplier === 'ml'
              ? [
                  { name: ['additional_costs'], type: 'fixed' },
                  { name: ['VTAM_choc_provisioire'], type: 'fixed' },
                  { name: ['operation_of_premises'], type: 'fixed' },
                  { name: ['intoxication_alimentaire'], type: 'fixed' },
                  {
                    name: ['neighboring_third_party_recourses'],
                    type: 'fixed'
                  }
                ]
              : []),
            ...(supplier !== 'ml'
              ? [
                  { name: ['extended_legal_protection_mrp'] },
                  { name: ['archives_capital'] },
                  { name: ['value_capital'] },
                  { name: ['art_capital'] },
                  { name: ['theft_capital'] },
                  { name: ['exploitation_loss'] },
                  { name: ['lent_goods'] }
                ]
              : [
                  { name: ['assistance'], type: 'fixed' },
                  { name: ['archives_capital'], type: 'fixed' },
                  { name: ['value_capital'], type: 'fixed' },
                  { name: ['theft_capital'], type: 'fixed' },
                  { name: ['object_of_value'], type: 'fixed' },
                  { name: ['lent_goods'], type: 'fixed' }
                ])
          ]}
          columns={[{ title: 'PACKAGE_1' }, { title: 'PACKAGE_2' }]}
        />
      )
    },
    ...(supplier === 'ml'
      ? [
          {
            name: ['display_legal_protection'],
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                onChange={(value) => handleLegalProtectionDisplayChange(value)}
              />
            ),
            step: 1
          },
          ...(displayLegalProtection
            ? [
                {
                  name: ['legal_protection_choice'],
                  tooltip: true,
                  input: (
                    <Radio.Group
                      buttonStyle="solid"
                      onChange={(e) =>
                        handleLegalProtectionChange(e.target.value)
                      }
                    >
                      {getLegalOptions()
                        .filter((option) => option.value > 0)
                        .map((option) => (
                          <Radio.Button key={option.value} value={option.value}>
                            {t(`contracts.form.options.${option.label}`)}
                          </Radio.Button>
                        ))}
                    </Radio.Group>
                  ),
                  help: exemptions.includes('legal_protection')
                    ? t('help.exemptions')
                    : null,
                  step: 1
                }
              ]
            : [])
        ]
      : []),
    ...(rCAllowed
      ? [
          {
            name: ['rc_pro'],
            tooltip: true,
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                onChange={(value) => handleRcProChange(value, supplier)}
              />
            ),
            step: 1
          }
        ]
      : []),
    ...((supplier === 'ml' || supplier === 'wk') && displayRcPro
      ? [
          {
            name: ['rc_pro_guarantee'],
            label: ['display_rc_pro_guarantee'],
            input: (
              <Radio.Group buttonStyle="solid">
                {filteredOptions.map((option) => (
                  <Radio.Button key={option} value={option}>
                    {`${option.toLocaleString()}€`}
                  </Radio.Button>
                ))}
              </Radio.Group>
            ),
            help: exemptions.includes('rc_pro_guarantee')
              ? t('help.exemptions')
              : null,
            step: 1
          },
          ...(formValues &&
          supplier === 'ml' &&
          (formValues?.rc_pro_guarantee > 150000 ||
            formValues?.self_entreprised)
            ? [
                {
                  name: ['self_entreprised'],
                  tooltip: true,
                  input: (
                    <Switch
                      checkedChildren={t('quotes.form.switch.yes')}
                      unCheckedChildren={t('quotes.form.switch.no')}
                      onChange={(value) => handleCompanyStatus(value)}
                    />
                  ),
                  step: 1
                }
              ]
            : []),
          ...(displayRcProOptions
            ? [
                ...(supplier === 'ml'
                  ? [
                      {
                        name: ['social_mandate_responsibility'],
                        input: <PreRequisitesSwitch />,
                        tooltip: true,
                        tooltipName: 'rcms_ml',
                        valuePropName: 'checked',
                        step: 1
                      }
                    ]
                  : []),
                {
                  name: ['exploitation_responsibility'],
                  input: <PreRequisitesSwitch />,
                  valuePropName: 'checked',
                  step: 1
                }
              ]
            : [])
        ]
      : []),
    ...(supplier === 'ml' && !isMRB
      ? [
          {
            name: ['display_mobility_guarantee'],
            tooltip: true,
            tooltipName: 'mobility_guarantee',
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                onChange={(value) => handleWantMobilityGuaranteeChange(value)}
              />
            ),
            step: 1
          }
        ]
      : []),
    ...(displayMobilityGuarantee
      ? [
          {
            name: ['mobility_guarantee'],
            label: 'mobility_guarantee_rates',
            tooltip: false,
            input: (
              <Radio.Group buttonStyle="solid">
                {getMobilityOptions()
                  .filter((option) => option.value > 0)
                  .map((option) => (
                    <Radio.Button key={option} value={option.value}>
                      {t(`contracts.form.options.${option.label}`)}
                    </Radio.Button>
                  ))}
              </Radio.Group>
            ),
            help: exemptions.includes('mobility_guarantee')
              ? t('help.exemptions')
              : null,
            step: 1
          }
        ]
      : []),
    ...(!isMRB && supplier === 'ml'
      ? [
          {
            name: ['display_business_loss_guarantee'],
            tooltip: true,
            tooltipName: 'business_loss_guarantee',
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                onChange={(value) =>
                  handleWantbusinessLossGuaranteeChange(value)
                }
              />
            ),
            step: 1
          }
        ]
      : []),
    ...(displaybusinessLossGuarantee
      ? [
          {
            name: ['business_loss_guarantee'],
            tooltip: false,
            input: (
              <Radio.Group
                buttonStyle="solid"
                onChange={(e) => handleBusinessLossChange(e.target.value)}
              >
                {getBusinessLossOptions()
                  .filter((option) => option.value > 0)
                  .map((option) => (
                    <Radio.Button key={option.value} value={option.value}>
                      {t(`contracts.form.options.${option.label}`)}
                    </Radio.Button>
                  ))}
              </Radio.Group>
            ),
            help: exemptions.includes('business_loss_guarantee')
              ? t('help.exemptions')
              : null,
            step: 1
          }
        ]
      : []),
    ...(displayWantMarketValueOption
      ? [
          {
            name: ['display_market_value'],
            tooltip: true,
            tooltipName: 'market_value_ml',
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                onChange={(value) => handleWantmarketValueChange(value)}
              />
            ),
            step: 1
          }
        ]
      : []),
    ...(displaymarketValue
      ? [
          {
            name: ['market_value'],
            input: (
              <Radio.Group buttonStyle="solid">
                {marketOptions
                  .filter((amount) => amount > 0)
                  .map((amount) => (
                    <Radio.Button key={amount} value={amount}>
                      {`${amount}€`}
                    </Radio.Button>
                  ))}
              </Radio.Group>
            ),
            tooltip: false,
            help: exemptions.includes('market_value')
              ? t('help.exemptions')
              : null,
            step: 1
          }
        ]
      : []),
    ...(supplier === 'ml'
      ? [
          {
            name: ['key_man_guarantee'],
            tooltip: true,
            tooltipName: 'key_man_guarantee',
            help:
              formValues?.deductible_amount === 0
                ? t('contracts.form.key_man_guarantee_unavailable')
                : null,
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
                disabled={formValues?.deductible_amount === 0}
              />
            ),
            step: 1
          }
        ]
      : []),
    {
      name: ['fraction_type'],
      rules: [{ required: true }],
      tooltip: true,
      input: (
        <Radio.Group buttonStyle="solid" disabled={isAmendment !== -1}>
          {getFrequencyOptions().map((frequency) => (
            <Radio.Button key={frequency} value={frequency}>
              {t(`contracts.form.options.${frequency}`)}
            </Radio.Button>
          ))}
        </Radio.Group>
      ),
      step: 1
    },
    ...(supplier === 'ml'
      ? [
          {
            name: ['90percent_tough_materials'],
            step: 2
          }
        ]
      : [
          {
            name: ['75percent_tough_materials'],
            step: 2
          }
        ]),
    {
      name: ['take_care_building'],
      step: 2
    },
    ...(supplier !== 'ml'
      ? [
          {
            name: ['2_incidents_max'],
            step: 2
          }
        ]
      : []),
    {
      name: ['more_500m_altitude'],
      step: 2
    },
    {
      name: ['close_help'],
      step: 2
    },
    {
      name: ['metropolitan_france'],
      step: 2
    },
    ...(supplier === 'ml'
      ? [
          {
            name: ['lci_sufficient'],
            nameParams: lciValue,
            step: 2
          },
          {
            name: ['half_timbering_structure'],
            step: 2
          }
        ]
      : []),
    ...(supplier !== 'ml'
      ? [
          {
            name: ['mall'],
            step: 2
          }
        ]
      : [
          {
            name: ['mall_big'],
            step: 2
          }
        ]),
    ...(supplier !== 'ml'
      ? [
          {
            name: ['castle'],
            step: 2
          }
        ]
      : []),
    ...(supplier !== 'wk'
      ? [
          {
            name: ['monument'],
            step: 2
          }
        ]
      : []),
    {
      name: ['ffb_1000_times'],
      step: 2
    },
    {
      name: ['embassy_bowling_disco'],
      step: 2
    },
    {
      name: ['religious_political'],
      step: 2
    },
    {
      name: ['industry_less_10m'],
      step: 2
    },
    ...(supplier !== 'ml'
      ? [
          {
            name: ['wood_plastic_nuclear_activity'],
            step: 2
          }
        ]
      : []),
    {
      name: ['disaffected'],
      step: 2
    },
    {
      name: ['walking_cooler'],
      step: 2
    },
    {
      name: ['28m_high'],
      step: 2
    },
    ...(supplier === 'ml'
      ? [
          {
            name: ['risk_has_graffitis_or_tags'],
            step: 2
          },
          {
            name: ['roof_non_standard_materials'],
            step: 2
          },
          {
            name: ['roof_built_or_replaced_after_2018'],
            step: 2
          },
          ...(formValues &&
          formValues?.roof_built_or_replaced_after_2018 === false
            ? [
                {
                  name: ['roof_is_flat_or_terrace'],
                  step: 2
                }
              ]
            : []),
          {
            name: ['fire_claim_last_60_months_subscriber_affiliates'],
            step: 2
          },
          {
            name: ['industrial_straw_activity'],
            step: 2
          },
          {
            name: ['construction_activity'],
            step: 2
          },
          {
            name: ['localized_qpv'],
            step: 2
          },
          {
            name: ['risk_declared_is_a_castle'],
            step: 2
          },
          {
            name: ['renovation'],
            step: 2
          },
          {
            name: ['zone_ind_com_art'],
            step: 2
          },
          {
            name: ['closed_more_60_days'],
            step: 2
          },
          {
            name: ['has_already_been_terminated_by_asx'],
            step: 2
          }
        ]
      : []),
    ...(supplier === 'wk'
      ? [
          {
            name: ['tags_roof_materials'],
            step: 2
          },
          {
            name: ['sea_side'],
            step: 2
          },
          {
            name: ['combustible_packaging'],
            step: 2
          },
          {
            name: ['straw_rubber_stock'],
            step: 2
          },
          {
            name: ['wholesale_business'],
            step: 2
          }
        ]
      : [])
  ];
  return {
    mrpFields: mrpFields.map((f) => {
      if (f.step === 2)
        return {
          ...f,
          input: (
            <PreRequisitesSwitch
              onChange={(val) => {
                setFormValues({
                  ...formValues,
                  [f.name[0]]: val
                });
                setCheckedVal(f.name[0]);
              }}
            />
          ),
          labelAlign: 'left',
          withDivider: false,
          valuePropName: 'checked',
          layout: preRequisitesLayout
        };
      return f;
    })
  };
};

export default useMRPFields;
